import { TextField } from '@radix-ui/themes'
import React from 'react'
import styled from 'styled-components'

import {
  buildInputDescribedBy,
  InputAndSubtitle,
  sharedTextFieldFocusStyles,
  StyledInputContainer,
  StyledLabel,
  StyledTextFieldSlot,
  SubtitleRowStyled,
  SubtitleTextStyled,
  TextValue,
} from './InputStyles'

export interface NumberInputProps extends React.DetailedHTMLProps<
  React.InputHTMLAttributes<HTMLInputElement>,
  HTMLInputElement
> {
  $label?: string
  $labelColor?: string
  $labelLocation?: 'top' | 'side'
  $subtitleText?: string
  $subtitleTextColor?: string
  $backgroundColor?: string
  $borderColor?: string
  mode?: 'read' | 'edit'
  units?: string
  ref?: React.ForwardedRef<HTMLInputElement>
}

const InputStyled = styled(TextField.Root)<NumberInputProps>`
  ${sharedTextFieldFocusStyles}

  & > input {
    padding: 0.25rem;
  }

  font-family: ${({ theme }) => theme.fontFamily.manrope};
  color: ${({ theme }) => theme.forecast.defaultText};
  background-color: ${({ $backgroundColor, theme }) =>
    $backgroundColor ? $backgroundColor : theme.forecast.cardGroupBackground};
  border-radius: 0.25rem;
  border: ${(props) =>
    props.$borderColor
      ? `1px solid ${props.$borderColor}`
      : `1px solid ${props.theme.forecast.border2}`};
  font-weight: 600;
  box-shadow: none !important;
  opacity: 1;

  &:hover {
    cursor: pointer;
  }

  &:focus {
    cursor: initial;
  }
`

export const NumberInput = React.forwardRef<HTMLInputElement, NumberInputProps>(
  (props, ref) => {
    const {
      $label,
      $labelColor,
      $labelLocation,
      $subtitleText,
      $subtitleTextColor,
      units,
      mode,
      'aria-describedby': ariaDescribedByFromProps,
      ...inputProps
    } = props

    const inputId = React.useId()
    const unitsId = React.useId()
    const subtitleId = React.useId()
    const describedBy = buildInputDescribedBy(
      units ? unitsId : undefined,
      mode !== 'read' && $subtitleText ? subtitleId : undefined,
      ariaDescribedByFromProps
    )

    return (
      <StyledInputContainer $labelLocation={$labelLocation}>
        {$label && (
          <StyledLabel $labelColor={$labelColor} htmlFor={inputId}>
            {$label}
          </StyledLabel>
        )}

        <InputAndSubtitle>
          {mode === 'read' ? (
            <TextValue id={inputId}>
              {props.defaultValue}
              {units && (
                <StyledTextFieldSlot side='right' id={unitsId}>
                  {units}
                </StyledTextFieldSlot>
              )}
            </TextValue>
          ) : (
            <InputStyled
              {...inputProps}
              ref={ref}
              id={inputId}
              aria-describedby={describedBy}>
              {units && (
                <StyledTextFieldSlot side='right' id={unitsId}>
                  {units}
                </StyledTextFieldSlot>
              )}
            </InputStyled>
          )}

          {mode !== 'read' && $subtitleText && (
            <SubtitleRowStyled>
              <SubtitleTextStyled
                id={subtitleId}
                $subtitleTextColor={$subtitleTextColor}>
                {$subtitleText}
              </SubtitleTextStyled>
            </SubtitleRowStyled>
          )}
        </InputAndSubtitle>
      </StyledInputContainer>
    )
  }
)
NumberInput.displayName = 'NumberInput'
